/**
 * Space Memories Manager
 *
 * Component for managing space-specific memories in the space settings dialog
 */

"use client"

import { FC, useState, useEffect } from "react"
import { Tables } from "@/supabase/types"
import { IconPlus, IconTrash, IconEdit, IconBrain, IconX } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog"
import { toast } from "sonner"
import { Skeleton } from "@/components/ui/skeleton"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"

interface SpaceMemoriesManagerProps {
  spaceId: string
}

export const SpaceMemoriesManager: FC<SpaceMemoriesManagerProps> = ({ spaceId }) => {
  const [memories, setMemories] = useState<Tables<"space_memories">[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingMemory, setEditingMemory] = useState<Tables<"space_memories"> | null>(null)
  const [memoryContent, setMemoryContent] = useState("")

  useEffect(() => {
    loadMemories()
  }, [spaceId])

  const loadMemories = async () => {
    try {
      setIsLoading(true)
      const response = await authenticatedFetch(`/api/spaces/${spaceId}/memories`, {
        cache: "no-store"
      })

      if (!response.ok) {
        throw new Error(await response.text())
      }

      setMemories((await response.json()) as Tables<"space_memories">[])
    } catch (error) {
      console.error("Error loading space memories:", error)
      toast.error("Failed to load space memories")
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddMemory = () => {
    setEditingMemory(null)
    setMemoryContent("")
    setIsDialogOpen(true)
  }

  const handleEditMemory = (memory: Tables<"space_memories">) => {
    setEditingMemory(memory)
    setMemoryContent(memory.content)
    setIsDialogOpen(true)
  }

  const handleDeleteMemory = async (memoryId: string) => {
    if (!confirm("Are you sure you want to delete this memory?")) {
      return
    }

    try {
      const response = await authenticatedFetch(
        `/api/spaces/${spaceId}/memories?memoryId=${encodeURIComponent(memoryId)}`,
        {
          method: "DELETE"
        }
      )

      if (!response.ok) {
        throw new Error(await response.text())
      }
      toast.success("Memory deleted")
      await loadMemories()
    } catch (error) {
      console.error("Error deleting memory:", error)
      toast.error("Failed to delete memory")
    }
  }

  const handleSaveMemory = async () => {
    if (!memoryContent.trim()) {
      toast.error("Memory content is required")
      return
    }

    try {
      if (editingMemory) {
        const response = await authenticatedFetch(`/api/spaces/${spaceId}/memories`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            id: editingMemory.id,
            content: memoryContent.trim()
          })
        })

        if (!response.ok) {
          throw new Error(await response.text())
        }
        toast.success("Memory updated")
      } else {
        const response = await authenticatedFetch(`/api/spaces/${spaceId}/memories`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            content: memoryContent.trim()
          })
        })

        if (!response.ok) {
          throw new Error(await response.text())
        }
        toast.success("Memory added")
      }

      setIsDialogOpen(false)
      setEditingMemory(null)
      setMemoryContent("")
      await loadMemories()
    } catch (error) {
      console.error("Error saving memory:", error)
      toast.error(editingMemory ? "Failed to update memory" : "Failed to add memory")
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skeleton className="size-[18px]" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-8 w-28" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border p-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="mt-1 h-3 w-1/3" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconBrain size={18} className="text-muted-foreground" />
          <span className="text-sm font-medium">
            {memories.length} {memories.length === 1 ? "memory" : "memories"}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddMemory}
          className="h-8"
        >
          <IconPlus size={16} className="mr-2" />
          Add Memory
        </Button>
      </div>

      {memories.length === 0 ? (
        <div className="rounded-lg border py-8 text-center">
          <IconBrain size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground mb-4 text-sm">
            No memories yet. Add memories to help AI provide better context in this space.
          </p>
          <Button variant="outline" size="sm" onClick={handleAddMemory}>
            <IconPlus size={16} className="mr-2" />
            Add First Memory
          </Button>
        </div>
      ) : (
        <div className="max-h-[400px] space-y-2 overflow-y-auto">
          {memories.map((memory) => (
            <div
              key={memory.id}
              className="hover:bg-muted/50 flex items-start justify-between rounded-lg border p-3 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm">{memory.content}</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {new Date(memory.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="ml-2 flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => handleEditMemory(memory)}
                >
                  <IconEdit size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive size-7"
                  onClick={() => handleDeleteMemory(memory.id)}
                >
                  <IconTrash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMemory ? "Edit Memory" : "Add Memory"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="memory-content">Memory Content</Label>
              <Textarea
                id="memory-content"
                value={memoryContent}
                onChange={(e) => setMemoryContent(e.target.value)}
                placeholder="Enter information that AI should remember about this space..."
                rows={4}
              />
              <p className="text-muted-foreground text-xs">
                This memory will be available to AI in all conversations within this space.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveMemory} disabled={!memoryContent.trim()}>
              {editingMemory ? "Update" : "Add"} Memory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
